/**
 * Utility functions for calculating and validating weekly export order limits
 */

// Weekly limits for export orders
export const EXPORT_WEEKLY_MAX_WEIGHT_KG = 1500;
export const EXPORT_WEEKLY_MAX_VOLUME_M3 = 11;

/**
 * Calculate total weight and volume for a list of orders
 * @param {Array} orders - Array of order objects with detalles
 * @returns {Object} - { totalWeight, totalVolume }
 */
export const calculateOrderTotals = (orders) => {
  let totalWeight = 0;
  let totalVolume = 0;

  orders.forEach(order => {
    if (order.detalles && Array.isArray(order.detalles)) {
      order.detalles.forEach(detalle => {
        const cantidad = detalle.cantidad_cajas || 0;
        const peso = detalle.peso_total_caja_kg || 0;
        const volumen = detalle.volumen_m3 || 0;

        totalWeight += cantidad * peso;
        totalVolume += cantidad * volumen;
      });
    }
  });

  return {
    totalWeight: parseFloat(totalWeight.toFixed(2)),
    totalVolume: parseFloat(totalVolume.toFixed(3))
  };
};

/**
 * Calculate totals for the current order being created/edited
 * @param {Array} detalles - Array of order detail objects
 * @returns {Object} - { weight, volume }
 */
export const calculateCurrentOrderTotals = (detalles) => {
  let weight = 0;
  let volume = 0;

  if (detalles && Array.isArray(detalles)) {
    detalles.forEach(detalle => {
      const cantidad = detalle.cantidad_cajas || 0;
      const peso = detalle.peso_total_caja_kg || 0;
      const volumen = detalle.volumen_m3 || 0;

      weight += cantidad * peso;
      volume += cantidad * volumen;
    });
  }

  return {
    weight: parseFloat(weight.toFixed(2)),
    volume: parseFloat(volume.toFixed(3))
  };
};

/**
 * Check if adding/updating an order would exceed weekly limits
 * @param {Array} existingOrders - Array of existing export orders for the week
 * @param {Array} currentOrderDetalles - Detalles of the order being created/edited
 * @param {number|null} currentOrderId - ID of order being edited (null for new orders)
 * @returns {Object} - { exceedsWeight, exceedsVolume, currentTotals, newTotals, limits }
 */
export const checkOrderLimits = (existingOrders, currentOrderDetalles, currentOrderId = null) => {
  // Filter out the current order if editing (to avoid double-counting)
  const ordersToCount = currentOrderId
    ? existingOrders.filter(order => order.id !== currentOrderId)
    : existingOrders;

  // Calculate existing totals
  const existing = calculateOrderTotals(ordersToCount);
  
  // Calculate current order totals
  const current = calculateCurrentOrderTotals(currentOrderDetalles);

  // Calculate new totals
  const newTotalWeight = existing.totalWeight + current.weight;
  const newTotalVolume = existing.totalVolume + current.volume;

  return {
    exceedsWeight: newTotalWeight > EXPORT_WEEKLY_MAX_WEIGHT_KG,
    exceedsVolume: newTotalVolume > EXPORT_WEEKLY_MAX_VOLUME_M3,
    currentTotals: existing,
    currentOrderTotals: current,
    newTotals: {
      weight: parseFloat(newTotalWeight.toFixed(2)),
      volume: parseFloat(newTotalVolume.toFixed(3))
    },
    limits: {
      weight: EXPORT_WEEKLY_MAX_WEIGHT_KG,
      volume: EXPORT_WEEKLY_MAX_VOLUME_M3
    }
  };
};

/**
 * Get color coding for limit status
 * @param {number} current - Current value
 * @param {number} limit - Maximum limit
 * @returns {string} - Color class: 'success', 'warning', or 'danger'
 */
export const getLimitColorStatus = (current, limit) => {
  const percentage = (current / limit) * 100;
  
  if (percentage < 80) return 'success'; // Green
  if (percentage <= 100) return 'warning'; // Yellow
  return 'danger'; // Red
};

/**
 * Format limit display text
 * @param {number} current - Current value
 * @param {number} limit - Maximum limit
 * @param {string} unit - Unit of measurement (kg, m³)
 * @returns {string} - Formatted text
 */
export const formatLimitDisplay = (current, limit, unit) => {
  const percentage = Math.round((current / limit) * 100);
  return `${current.toLocaleString('es-CL')} ${unit} / ${limit.toLocaleString('es-CL')} ${unit} (${percentage}%)`;
};
